import type { SchoolLevel } from "./catalog";
import legacyCurriculum from './curriculum-legacy.json';

export type CurriculumChapter = { id: string; title: string };
export type CurriculumTheme = { id: string; title: string; chapters: CurriculumChapter[] };
export type Curriculum = Partial<Record<SchoolLevel, CurriculumTheme[]>>;

const chapter = (id: string, title: string): CurriculumChapter => ({ id, title });
const theme = (id: string, title: string, chapters: CurriculumChapter[]): CurriculumTheme => ({ id, title, chapters });

export const defaultCurriculum: Curriculum = {
  "Seconde": [
    theme("2de-matiere", "Constitution et transformations de la matière", [
      chapter("2de-matiere-01", "Corps purs et mélanges"),
      chapter("2de-matiere-02", "Solutions aqueuses"),
      chapter("2de-matiere-03", "De l’atome à l’élément chimique"),
      chapter("2de-matiere-04", "Vers des entités plus stables"),
      chapter("2de-matiere-05", "Quantité de matière"),
      chapter("2de-matiere-06", "Transformation physique"),
      chapter("2de-matiere-07", "Transformation chimique"),
      chapter("2de-matiere-08", "Transformation nucléaire"),
    ]),
    theme("2de-mouvement", "Mouvement et interactions", [
      chapter("2de-mouvement-09", "Description des mouvements"),
      chapter("2de-mouvement-10", "Modéliser une action mécanique sur un système"),
      chapter("2de-mouvement-11", "Principe d’inertie"),
    ]),
    theme("2de-ondes", "Ondes et signaux", [
      chapter("2de-ondes-12", "Émission et perception d’un son"),
      chapter("2de-ondes-13", "Spectres d’émission"),
      chapter("2de-ondes-14", "Réfraction et réflexion de la lumière"),
      chapter("2de-ondes-15", "Lentilles minces convergentes"),
      chapter("2de-ondes-16", "Lois de l’électricité"),
    ]),
  ],
  "1re Spé": [
    theme("1spe-matiere", "Constitution et transformations de la matière", [
      chapter("1spe-matiere-01", "La mole"),
      chapter("1spe-matiere-02", "Réactions d’oxydoréduction"),
      chapter("1spe-matiere-03", "Tableau d’avancement"),
      chapter("1spe-matiere-04", "Titrages colorimétriques"),
      chapter("1spe-matiere-05", "De la structure à la polarité d’une entité chimique"),
      chapter("1spe-matiere-06", "Cohésion de la matière"),
      chapter("1spe-matiere-07", "Structure des entités organiques"),
      chapter("1spe-matiere-08", "Synthèse"),
      chapter("1spe-matiere-09", "Réactions de combustion"),
    ]),
    theme("1spe-mouvement", "Mouvement et interactions", [
      chapter("1spe-mouvement-10", "Interactions, forces et champs"),
      chapter("1spe-mouvement-11", "Description d’un fluide au repos"),
      chapter("1spe-mouvement-12", "Mouvement d’un système"),
    ]),
    theme("1spe-energie", "L’énergie : conversions et transferts", [
      chapter("1spe-energie-13", "L’énergie des systèmes électriques"),
      chapter("1spe-energie-14", "Aspects énergétiques des phénomènes mécaniques"),
    ]),
    theme("1spe-ondes", "Ondes et signaux", [
      chapter("1spe-ondes-15", "Ondes mécaniques"),
      chapter("1spe-ondes-16", "Lentilles minces convergentes"),
      chapter("1spe-ondes-17", "Couleurs"),
      chapter("1spe-ondes-18", "Lumière : ondes et propriétés"),
    ]),
  ],
  "Tle Spé": [
    theme("tspe-matiere", "Constitution et transformations de la matière", [
      chapter("tspe-matiere-01", "Transformations acide-base"),
      chapter("tspe-matiere-02", "Méthodes physiques d’analyse"),
      chapter("tspe-matiere-03", "Méthodes chimiques d’analyse"),
      chapter("tspe-matiere-04", "Modélisation macroscopique de l’évolution d’un système"),
      chapter("tspe-matiere-05", "Modélisation microscopique de l’évolution d’un système"),
      chapter("tspe-matiere-06", "Évolution d’un système, siège d’une transformation nucléaire"),
      chapter("tspe-matiere-07", "Sens d’évolution spontanée d’un système chimique"),
      chapter("tspe-matiere-08", "Force des acides et des bases"),
      chapter("tspe-matiere-09", "Forcer l’évolution d’un système"),
      chapter("tspe-matiere-10", "Synthèses organiques"),
    ]),
    theme("tspe-mouvement", "Mouvement et interactions", [
      chapter("tspe-mouvement-11", "Mouvement et deuxième loi de Newton"),
      chapter("tspe-mouvement-12", "Mouvement dans un champ uniforme"),
      chapter("tspe-mouvement-13", "Mouvement dans un champ de gravitation"),
      chapter("tspe-mouvement-14", "Modélisation de l’écoulement d’un fluide"),
    ]),
    theme("tspe-energie", "L’énergie : conversions et transferts", [
      chapter("tspe-energie-15", "Premier principe de la thermodynamique et bilan énergétique"),
      chapter("tspe-energie-16", "Transferts thermiques"),
    ]),
    theme("tspe-ondes", "Ondes et signaux", [
      chapter("tspe-ondes-17", "Sons et effet Doppler"),
      chapter("tspe-ondes-18", "Diffraction et interférences"),
      chapter("tspe-ondes-19", "Lunette astronomique"),
      chapter("tspe-ondes-20", "La lumière : un flux de photons"),
      chapter("tspe-ondes-21", "Dynamique du dipôle RC"),
    ]),
  ],
  "1re ES": [
    theme("1es-theme-1", "Une longue histoire de la matière", [
      chapter("1es-theme-1-01", "Un niveau d’organisation : les éléments chimiques"),
      chapter("1es-theme-1-02", "Des édifices ordonnés : les cristaux"),
    ]),
    theme("1es-theme-2", "Le Soleil, notre source d’énergie", [
      chapter("1es-theme-2-04", "Le rayonnement solaire"),
      chapter("1es-theme-2-05", "Bilan radiatif terrestre"),
    ]),
    theme("1es-theme-3", "La Terre, un astre singulier", [
      chapter("1es-theme-3-08", "La forme de la Terre"),
    ]),
    theme("1es-theme-4", "Son et musique, porteurs d’information", [
      chapter("1es-theme-4-11", "Son et musique"),
      chapter("1es-theme-4-12", "Le son, une information à coder"),
      chapter("1es-theme-4-13", "Entendre et protéger son audition"),
    ]),
  ],
};

export function cloneCurriculum(curriculum: Curriculum): Curriculum {
  return Object.fromEntries(Object.entries(curriculum).map(([level, themes]) => [
    level,
    (themes ?? []).map((item) => ({ ...item, chapters: item.chapters.map((entry) => ({ ...entry })) })),
  ])) as Curriculum;
}

export function curriculumForLevel(curriculum: Curriculum, level: SchoolLevel) {
  return curriculum[level] ?? [];
}

/** Central empty lists are authoritative; only local legacy data is upgraded. */
export function curriculumFromResponse(body: {curriculum?: Curriculum | null; managedByAkyo?: boolean; source?: string}): Curriculum {
  return body.managedByAkyo || body.source?.startsWith('akyo')
    ? cloneCurriculum(body.curriculum ?? {}) : resolveCurriculum(body.curriculum);
}

export function chaptersForTheme(curriculum: Curriculum, level: SchoolLevel, themeTitle: string) {
  return curriculumForLevel(curriculum, level).find((item) => item.title === themeTitle)?.chapters ?? [];
}

export function newCurriculumId(prefix: string) {
  return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

/** Upgrade only untouched legacy levels. Deliberate edits and empty lists are preserved. */
export function resolveCurriculum(saved: Curriculum | null | undefined): Curriculum {
 const result=cloneCurriculum(saved??{});
 const signature=(themes:CurriculumTheme[]|undefined)=>JSON.stringify(themes?.map(t=>[t.id,t.title,t.chapters.map(c=>[c.id,c.title])]));
 for(const level of Object.keys(defaultCurriculum) as SchoolLevel[]){
  const previous=legacyCurriculum[level as keyof typeof legacyCurriculum];
  if(!Object.hasOwn(result,level)||signature(result[level])===signature(previous)){
   result[level]=cloneCurriculum(defaultCurriculum)[level];
  }
 }
 return result;
}

export function curriculumChapterNumber(chapter:CurriculumChapter):number|undefined {
 const match=/^(?:2de|1spe|tspe|1es)-(?:matiere|mouvement|energie|ondes|theme-[1-4])-(\d{2})$/.exec(chapter.id);
 return match?Number(match[1]):undefined;
}
export function curriculumChapterLabel(chapter:CurriculumChapter):string {
 const number=curriculumChapterNumber(chapter);
 return number?`Chapitre ${number} — ${chapter.title}`:chapter.title;
}
